import { SelectOption } from "../utilities/dropDownOptions";

export const currentSRPSchemaVersion = "0.1.1";

export interface SRPDSelectionCriteriaListInterface {
    modelYear: string;
    programCode: string;
    ncPartNumber: string;
    mfalCode: string;
    did: string;
}

export interface SRPDClusterListInterface {
    clusterName: string;
    changeLevel: string;
    clusterVersion: string;
    swPartNumber: string;
    clusterUrl: string; 
    clusterType: string;
    configurationType: string;
    ecuExtractVersion: string;
    f163D2SpecVersion: string;
    part2SpecUrl: string;
    wersConcernNum: string;
    f113ECUAssemblyNum: string[];
    f111CoreAssemblyNum: string[];
    selectionCriteriaList: SRPDSelectionCriteriaListInterface[];
    actionType: string;
    updateType: string;
    udsMethod: string[];
    inhibitRequired: boolean;
    sessionSequence: string;
    swDownloadMethod: string;
    swInstallationSequence: number;
    updateTimeInSeconds: number;
    assemblyCritical: boolean;
    productionReleaseIntent: boolean;
    resetDelay: number;
    swSignRequest: string;
    swSigningId: string;
    ucmPackagePath: string;
    programmingMethod: string;
    // non-schema fields
    // VADRnIVSInformation?: {
    //     createIcePack: boolean;
    //     softwareCategory: string;
    //     icepackLineage: string[];
    //     dependencyName: string;
    //     dependencyVersion: string;
    //     relationshipType: string;
    //     relatedIcepack: string[];
    // };
}


// CLusterList state interface should match SRPDClusterListInterface but also include VADRnIVSInformation
export interface StateClusterListInterface extends SRPDClusterListInterface {
    clusterUrlOptions?: SelectOption[]; // for dynamic dropdown options
    part2UrlOptions?: SelectOption[]; // for dynamic dropdown options
    // nonSchema fields
    vbfType?: string;
    VADRnIVSInformation?: {
        createIcePack: boolean;
        softwareCategory: string;   
        icepackLineage: string[];
        dependencyName: string;
        dependencyVersion: string;
        relationshipType: string;
        relatedIcepack: string[];
    };
    isProcessingSignFileMetadata?: boolean; // to indicate if we're currently processing the sign file metadata
    // nonSchema fields end
}



export interface SRPDVariantListInterface {
    variantName: string;
    logicalDiagAddress: string;
    ucmSubordinateId: string;
    flashingAdapterAddress: string;
    arxmlRequired: boolean;
    vspRequired: boolean;
    otaCapable: boolean;
    flashCapability: string;
    commsProtocol: string[]; // format changed
    publicTopology: string;
    privateTopology: string;
    ethernetTopology: string[]; // format changed
    asilRating: string;
    // nonSchema fields start
    // supplier?: string;
    // nonSchema fields end
    clusterList: SRPDClusterListInterface[];
}


export interface StateVariantListInterface extends SRPDVariantListInterface {
    // nonSchema fields
    supplier?: string;
    // nonSchema fields end
    clusterList: StateClusterListInterface[];
}

export interface SRPDMachineListInterface {
    machineName: string;
    machineType: string;    
    canDiagRxId: string;
    canDiagTxId: string;
    variantList: SRPDVariantListInterface[];
}

export interface StateMachineListInterface extends SRPDMachineListInterface {
    // nonSchema fields
    variantList: StateVariantListInterface[];
}

export interface SRPDModuleInformationInterface {
    moduleName: string;
    vehiclePrograms: string[];
    lveas: string[];
    gpdsMilestone: string;
    publicRoadWorthy: boolean;
    criticalAttributesImpacted: string[];
    cmaEnabled: boolean;
}

export interface SRPDPreAssemblyImageClusterInterface {
    clusterName: string;
    clusterVersion: string;
}

export interface SRPDPreAssemblyImageInterface {
    preassemblyImageName: string;
    preassemblyImageVersion: string;
    preassemblyImageUrl: string;    
    f111coreAssemblyNum: string[];
    ucmPackagePath: string;
    clusterList: SRPDPreAssemblyImageClusterInterface[];
}


export interface StateSRPdParentLevelInterface {
    srpdName: string;
    srpdVersion: string;
    srpAssignee: string;
    srpWatchers: string[];
    srpSchemaVersion: string;
    electricalArchitecture: string[];
    releaseObjectKey: string;
    fixVersion: string;
}


export interface SRPSchemaInterface {
    srpdName: string;
    srpdVersion: string;
    srpAssignee: string;
    srpWatchers: string[];
    srpSchemaVersion: string;
    electricalArchitecture: string[];
    moduleInformation: SRPDModuleInformationInterface;
    machineList: SRPDMachineListInterface[];
    preassemblyImage: SRPDPreAssemblyImageInterface;
}

export interface StateSRPSchemaInterface extends StateSRPdParentLevelInterface{
    moduleInformation: SRPDModuleInformationInterface;
    machineList: StateMachineListInterface[];
    preassemblyImage: SRPDPreAssemblyImageInterface;

}

export const srpdParentLevelTemplate = {
    "srpdName": "",
    "srpdVersion": "",
    "srpAssignee": "", 
    "srpWatchers": [],
    "srpSchemaVersion": currentSRPSchemaVersion,
    "electricalArchitecture": [],
    "releaseObjectKey": "",
    "fixVersion": "",
} as StateSRPdParentLevelInterface;

export const selectionCriteriaListTemplate = {
    modelYear: "",
    programCode: "",
    ncPartNumber: "",
    mfalCode: "",
    did: "",
} as SRPDSelectionCriteriaListInterface;

// NOTE: UPDATE THIS WITH SCHEMA CHANGES
export const srpTemplate = {
    ...srpdParentLevelTemplate,
    "moduleInformation": {
        "moduleName": "",
        "vehiclePrograms": [],
        "lveas": [],
        "gpdsMilestone": "",
        "publicRoadWorthy": false,
        "criticalAttributesImpacted": ["NOT_APPLICABLE"],
        "cmaEnabled": false,
    },
    "machineList": [
        {
            "machineName": "",
            "machineType": "",
            "canDiagRxId": "",
            "canDiagTxId": "",
            "variantList": [
                {
                    "variantName": "",
                    "logicalDiagAddress": "",
                    "ucmSubordinateId": "",
                    "flashingAdapterAddress": "",
                    "arxmlRequired": false,
                    "vspRequired": false,
                    "otaCapable": false,
                    "flashCapability": "",
                    "commsProtocol": [], // format changed
                    "publicTopology": "",
                    "privateTopology": "",
                    "ethernetTopology": [], // format changed
                    "asilRating": "",
                    // nonSchema fields start
                    "supplier": "",
                    // nonSchema fields end
                    "clusterList": [
                        {
                            "clusterName": "",
                            "changeLevel": "", // just added
                            "clusterVersion": "",
                            "swPartNumber": "",
                            "clusterUrl": "",
                            "clusterType": "",
                            "configurationType": "",
                            "ecuExtractVersion": "",
                            "f163D2SpecVersion": "N/A",
                            "part2SpecUrl": "",
                            "wersConcernNum": "",
                            "f113ECUAssemblyNum": [],
                            "f111CoreAssemblyNum": [],
                            "selectionCriteriaList": [
                                structuredClone(selectionCriteriaListTemplate)
                            ],
                            "actionType": "",
                            "updateType": "",
                            "udsMethod": [],
                            "inhibitRequired": false,
                            "sessionSequence": "",
                            "swDownloadMethod": "",
                            "swInstallationSequence": 0,
                            "updateTimeInSeconds": 0,
                            "assemblyCritical": false,
                            "productionReleaseIntent": false,
                            "resetDelay": 1,
                            "swSignRequest": "",
                            "swSigningId": "",
                            "ucmPackagePath": "",
                            "programmingMethod": "",

                            // // non-schema fields
                            "vbfType": "",
                            "VADRnIVSInformation": {
                                "createIcePack": false,
                                "softwareCategory": "",
                                "icepackLineage": [],
                                "dependencyName": "",
                                "dependencyVersion": "",
                                "relationshipType": "",
                                "relatedIcepack": [],
                            }
                        }
                    ]
                }
            ],
        }
    ],
    "preassemblyImage": {
            "preassemblyImageName": "",
            "preassemblyImageVersion": "",
            "preassemblyImageUrl": "",
            "f111coreAssemblyNum": [],
            "ucmPackagePath": "",
            "clusterList": [
                {
                    "clusterName": "",
                    "clusterVersion": "",
                }
            ]
        }
} as StateSRPSchemaInterface;